import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface GasInfraProperties {
  id: string;
  type: string;
  name: string | null;
  operator: string | null;
  pipeline_company: string | null;
  capacity_dth_day: number | null;
  horsepower: number | null;
  num_units: number | null;
  connecting_companies: string | string[] | null;
  facility_type: string | null;
  status: string | null;
  state: string | null;
  county: string | null;
  methane_mt: number | null;
  source: string | null;
}

interface GasInfraDetailProps {
  facility: GasInfraProperties;
  onClose: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  compressor: { label: 'Compressor Station', color: designTokens.colors.gasInfraCompressor },
  receipt_delivery: { label: 'Receipt/Delivery Point', color: designTokens.colors.gasInfraReceiptDelivery },
  interconnect: { label: 'Pipeline Interconnect', color: designTokens.colors.gasInfraInterconnect },
  gas_storage: { label: 'Gas Storage Facility', color: designTokens.colors.gasInfraStorage },
};

const STATUS_COLORS: Record<string, string> = {
  'IN SERVICE': '#16a34a',
  'ACTIVE': '#16a34a',
  'OPERATIONAL': '#16a34a',
  'OUT OF SERVICE': '#6b7280',
  'STANDBY': '#d97706',
  'RETIRED': '#6b7280',
};

function formatCapacity(dthDay: number): string {
  if (dthDay >= 1000000) return `${(dthDay / 1000000).toFixed(1)}M Dth/d`;
  if (dthDay >= 1000) return `${(dthDay / 1000).toFixed(0)}K Dth/d`;
  return `${dthDay.toLocaleString()} Dth/d`;
}

function parseConnecting(val: string | string[] | null): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch { return [val]; }
}

export const GasInfraDetail: React.FC<GasInfraDetailProps> = ({ facility, onClose }) => {
  const { theme } = useTheme();
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const f = facility;
  const config = TYPE_CONFIG[f.type] || { label: f.type, color: '#6b7280' };
  const statusColor = STATUS_COLORS[(f.status || '').toUpperCase()] || '#6b7280';
  const connecting = parseConnecting(f.connecting_companies);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            {f.name || 'Unknown Facility'}
          </h2>
          <div style={{
            marginTop: '4px', display: 'inline-block',
            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
            background: config.color + '20', color: config.color,
            borderRadius: '4px',
          }}>
            {config.label}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Status badge */}
        {f.status && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{
              display: 'inline-block', padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: statusColor + '20', color: statusColor, borderRadius: '4px',
            }}>
              {f.status}
            </span>
          </div>
        )}

        {/* Key metric — HP for compressors, capacity for others */}
        {f.type === 'compressor' && f.horsepower != null && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Certified Horsepower</span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 600, color: theme.text,
              }}>{f.horsepower.toLocaleString()} HP</span>
            </div>
            {f.num_units != null && (
              <div style={{ fontSize: '10px', color: theme.textTertiary }}>
                {f.num_units} compressor unit{f.num_units !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {f.type === 'compressor' && f.methane_mt != null && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Methane Emissions</span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 600, color: theme.text,
              }}>{Math.round(f.methane_mt).toLocaleString()} MT/yr</span>
            </div>
            <div style={{ fontSize: '10px', color: theme.textTertiary }}>
              EPA GHGRP Subpart W (2023 reporting year)
            </div>
          </div>
        )}

        {f.capacity_dth_day != null && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>
                {f.type === 'gas_storage' ? 'Max Deliverability' : 'Max Capacity'}
              </span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '13px', fontWeight: 600, color: theme.text,
              }}>{formatCapacity(f.capacity_dth_day)}</span>
            </div>
          </div>
        )}

        {/* Operator / Pipeline */}
        <Section title="Operator">
          {f.operator && <Field label="Operator" value={f.operator} />}
          {f.pipeline_company && f.pipeline_company !== f.operator && (
            <Field label="Pipeline Company" value={f.pipeline_company} />
          )}
          {f.facility_type && f.facility_type !== f.type && (
            <Field label="Facility Type" value={f.facility_type} />
          )}
        </Section>

        {/* Connecting companies (receipt/delivery and interconnects) */}
        {connecting.length > 0 && (
          <Section title="Connections">
            {connecting.map((c, i) => (
              <Field key={i} label={i === 0 ? 'Connecting Pipeline' : `Connection ${i + 1}`} value={c} />
            ))}
          </Section>
        )}

        {/* Storage-specific fields */}
        {f.type === 'gas_storage' && (
          <Section title="Storage">
            <Field label="Storage Type" value={f.facility_type} />
          </Section>
        )}

        {/* Location */}
        <Section title="Location">
          <Field label="County" value={f.county ? `${f.county} Co.` : null} />
          <Field label="State" value={f.state} />
        </Section>

        {/* Data Source — expandable */}
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => setSourceExpanded(!sourceExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: '#4b556320', color: '#8A9CAA',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
            }}
          >
            <Info size={10} />
            {f.source === 'EPA_GHGRP' ? 'EPA GHGRP 2023' : f.type === 'compressor' ? 'HIFLD Feb 2023' : 'HIFLD Sep 2020'}
            {sourceExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          {sourceExpanded && (
            <div style={{
              marginTop: '6px', padding: '8px', fontSize: '10px',
              border: `1px solid ${theme.borderLight}`,
              borderRadius: '6px', lineHeight: 1.6,
            }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: '4px' }}>
                {f.source === 'EPA_GHGRP'
                  ? 'EPA Greenhouse Gas Reporting Program (GHGRP)'
                  : 'Homeland Infrastructure Foundation-Level Data (HIFLD)'}
              </div>
              <div style={{ color: theme.textTertiary }}>
                {f.source === 'EPA_GHGRP'
                  ? 'Subpart W — Petroleum and Natural Gas Systems'
                  : 'Compiled by Oak Ridge National Laboratory (ORNL)'}
              </div>
              <div style={{ color: theme.textTertiary }}>
                Sources: {f.source === 'EPA_GHGRP'
                  ? 'Facility self-reported emissions, EPA verification'
                  : f.type === 'compressor'
                    ? 'EPA Envirofacts, FERC Form 2, corporate disclosures, NAIP imagery'
                    : 'EIA capacity data, pipeline company informational postings'}
              </div>
              <div style={{ color: theme.textTertiary }}>
                Vintage: {f.source === 'EPA_GHGRP' ? '2023 reporting year' : f.type === 'compressor' ? 'February 2023' : 'September 2020'}
              </div>
              {f.type === 'compressor' && !f.horsepower && f.source !== 'EPA_GHGRP' && (
                <div style={{ color: '#d97706', marginTop: '4px' }}>
                  HP data unavailable — gathering/midstream stations are not covered by FERC Form 2
                </div>
              )}
              <div style={{ marginTop: '4px' }}>
                <a
                  href={f.source === 'EPA_GHGRP'
                    ? 'https://ghgdata.epa.gov/ghgp/main.do'
                    : 'https://hifld-geoplatform.opendata.arcgis.com/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#D49880', textDecoration: 'underline', fontSize: '10px' }}
                >
                  {f.source === 'EPA_GHGRP' ? 'EPA GHGRP Data Explorer' : 'HIFLD Open Data Portal'}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
